import { getAppConfig } from './app-config.js';
/**
 * Base of the current application. This will try it best to catch all cases
 *
 * * If the base metadata is defined, it will be used. If not...
 * * The site HREF will be used
 */
const base = new URL(document.baseURI ||
    /* istanbul ignore next: fallback for browser that does not has baseURI */
    window.location.href ||
    /* istanbul ignore next: fallback for IE 11 */
    '');
// Store all the subscriptions registered
const subscribers = new Set();
/**
 * Handles changes of url from any source and call all subscribers.
 * @param newUrl - Url string
 */
function onURLChange(newUrl) {
    for (const iterator of Array.from(subscribers)) {
        iterator(newUrl);
    }
}
/**
 * This is a Proxy API handle to intercept calls to `window.history.pushState` and `window.history.replaceState`
 */
const handler = {
    apply: (target, thisArg, 
    // eslint-disable-next-line @rushstack/no-new-null, @typescript-eslint/no-explicit-any
    args) => {
        if (args[2] && args[2] !== window.location.pathname) {
            onURLChange(args[2]);
        }
        target.apply(thisArg, args);
    },
};
// Use Proxy to intercept all calls to `window.history.pushState` and `window.history.replaceState`
window.history.pushState = new Proxy(window.history.pushState, handler);
window.history.replaceState = new Proxy(window.history.replaceState, handler);
/**
 * Catch all hash changes (angular.js uses it for routing) and get the route from it
 */
window.addEventListener('hashchange', (event) => {
    const { urlSeparator } = getAppConfig();
    onURLChange(
    // NewURL is not supported on IE11 so it fallback to window.location.hash
    (event.newURL || /* istanbul ignore next: fallback for IE 11 */ window.location.hash).replace(`${base.toString()}${urlSeparator !== null && urlSeparator !== void 0 ? urlSeparator : ''}`, ''));
});
/**
 * Adds a subscription to the Framework URL change.
 * @public
 * @remarks Framework URL is the URL part that will be handled by the SPA framework router.
 * @param fn - Function to get called on each Framework URL change
 * @returns Function that can be called to unsubscribe the subscription
 */
export function subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
}
/**
 * Removes a subscription based on the subscription callback
 * @public
 * @remarks If the callback function is not register, it will not do anything. Framework URL is the URL part that will be handled by the SPA framework router.
 * @param fn - Callback function
 */
export function unsubscribe(fn) {
    if (subscribers.has(fn)) {
        subscribers.delete(fn);
    }
}
/**
 * Retrieves the current URL of the application (framework URL removing the base application URL)
 * @remarks Framework URL is the URL part that will be handled by the SPA framework router.
 * @public
 * @returns Current Framework URL
 */
export function getCurrentApplicationURL() {
    const { urlSeparator } = getAppConfig();
    if (urlSeparator) {
        // Using array destructuring I am removing the first part of the URL until the first occurrence of `urlSeparator`
        const [, ...url] = window.location.href.split(urlSeparator);
        // Joining back all the other values will generate the same URL but without the first part and the first `urlSeparator`
        return url.join(urlSeparator);
    }
    const { pathname } = new URL(window.location.href);
    return pathname;
}
